import React from "react";
import TreeDropdown, { TreeDropdownOption } from "../tree-dropdown";
import { Dialog, DialogTitle, Typography, DialogContent, FormControl, InputLabel, DialogActions, Button, CircularProgress, FormControlLabel, Checkbox, useMediaQuery } from "@mui/material";
import theme from "../../util/theme/theme";
import BottomSheetDialog from "./mobile/mobile-bottom-sheet";

interface ReassignCategoryProps {
    open: boolean;
    transactionCount: number;
    categoryOptions: TreeDropdownOption[];
    oldCategoryId: number | null;
    onCancel: () => void;
    onConfirm: (newCategoryId: number, applyDefaultTaxes: boolean) => void;
    loading?: boolean;
}

const ReassignCategoryModal: React.FC<ReassignCategoryProps> = ({
    open,
    transactionCount,
    categoryOptions,
    oldCategoryId,
    onCancel,
    onConfirm,
    loading = false,
}) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedCategory, setSelectedCategory] = React.useState<string | null>(null);
    const [applyDefaultTaxes, setApplyDefaultTaxes] = React.useState(false);

    React.useEffect(() => {
        if (!open) {
            setSelectedCategory(null);
            setApplyDefaultTaxes(false);
        }
    }, [open]);

    const handleConfirm = () => {
        if (selectedCategory && selectedCategory !== String(oldCategoryId)) {
            onConfirm(Number(selectedCategory), applyDefaultTaxes);
        }
    };

    const isDisabled = !selectedCategory || selectedCategory === String(oldCategoryId) || loading;

    const content = (
        <>
            <DialogTitle>
                Reassign <span style={{ color: 'var(--color-primary)' }}>{transactionCount.toLocaleString()} transactions</span> to a new category
            </DialogTitle>
            <DialogContent>
                <Typography fontSize={15} color="var(--color-text-muted)" sx={{ mt: 2 }}>
                    Category <span style={{ color: '#F44336' }}>*</span>
                </Typography>
                <TreeDropdown
                    options={categoryOptions}
                    value={selectedCategory ?? null}
                    onChange={(selectedId) => {
                        if (selectedId !== String(oldCategoryId)) {
                            setSelectedCategory(selectedId);
                        }
                    }}
                    placeholder="Select a new category"
                    height={40}
                />
                <FormControlLabel
                    sx={{ mt: 2 }}
                    control={
                        <Checkbox
                            checked={applyDefaultTaxes}
                            onChange={(e) => setApplyDefaultTaxes(e.target.checked)}
                            sx={{ color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' } }}
                        />
                    }
                    label={<Typography fontSize={13.5} color="var(--color-text-muted)">Also replace applied taxes on these transactions with the new category's default taxes</Typography>}
                />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button onClick={onCancel} variant="contained" disabled={loading} sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-container-background)' } }}>
                    Cancel
                </Button>
                <Button onClick={handleConfirm} variant="contained" disabled={isDisabled} sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-primary)' }, '&:disabled': { background: '#cccccc' } }}>
                    {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Reassign'}
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={onCancel} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
            {content}
        </Dialog>
    );
};

export default ReassignCategoryModal;